import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useGetChangeSets from '../../../../api/useGetChangeSets';
import { ChangeSet } from '../../../../types/ChangeSetTypes';
import LoadingSpinner from '../../../shared/LoadingSpinner';
import { formatDateTime } from '../../../../utils/dateFormatters';
import { SectionCard, SectionTitle } from '../../Section.styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClock } from '@fortawesome/free-solid-svg-icons';
import { ChangeSetStatusEnum } from '../../../../enums/ChangeSetStatusEnum';
import {
    FilterBar,
    FilterLabel,
    BorderedContainer,
    TableScrollContainer,
    HeaderRow,
    ChangeRow,
    EditSelect,
    EditInput,
    HideOverflow,
    TypeBadge,
    StatusBadge,
    LoadMoreButton,
    Error,
    NoData,
    UserCell,
    UserAvatar,
} from '../../../../pages/changelog/ChangeLogPage.styles';
import { getInitials } from '../../../../utils/userFormatters';

interface ProductChangeLogSectionProps {
    /** The display name used to filter changesets (product's tradeName or category name) */
    productDisplayName: string;
}

type ChangeLogEntry = ChangeSet;

const INITIAL_LOAD = 200;
const LOAD_MORE_COUNT = 100;

const generateYearOptions = (): number[] => {
    const currentYear = new Date().getFullYear();
    const startYear = 2020;
    const years: number[] = [];
    for (let year = currentYear; year >= startYear; year--) {
        years.push(year);
    }
    return years;
};

const ProductChangeLogSection: React.FC<ProductChangeLogSectionProps> = ({ productDisplayName }) => {
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState<number>(currentYear);
    const [selectedType, setSelectedType] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    const [selectedSort, setSelectedSort] = useState('date-desc');
    const navigate = useNavigate();

    const yearOptions = useMemo(() => generateYearOptions(), []);

    const { changeSets, totalCount, loadedCount, loading, error, getChangeSets, reset } = useGetChangeSets();

    // Navigate to the global changelog detail page on row click
    const handleRowClick = (id: number) => {
        navigate(`/changelog?changeLogId=${id}`);
    };

    // Load all changes for the year, using displayInfo for server-side pre-filtering
    useEffect(() => {
        if (!productDisplayName) return;
        reset();
        const sortOrder = selectedSort.endsWith('-asc') ? 'asc' : 'desc';
        getChangeSets({
            skip: 0,
            take: INITIAL_LOAD,
            sortBy: 'DateTimeUtc',
            sortOrder,
            year: selectedYear,
            displayInfo: productDisplayName,
        });
    }, [getChangeSets, selectedYear, selectedSort, reset, productDisplayName]);

    // Load more handler
    const handleLoadMore = () => {
        const sortOrder = selectedSort.endsWith('-asc') ? 'asc' : 'desc';
        getChangeSets({
            skip: loadedCount,
            take: LOAD_MORE_COUNT,
            sortBy: 'DateTimeUtc',
            sortOrder,
            year: selectedYear,
            displayInfo: productDisplayName,
        });
    };

    // Client-side exact match filter on displayInformation to ensure only this product's changes show
    const allChangeLogEntries: ChangeLogEntry[] = useMemo(() => {
        const items = Array.isArray(changeSets) ? changeSets : [];
        return items.filter((entry) => entry.displayInformation === productDisplayName);
    }, [changeSets, productDisplayName]);

    const types = ['Created', 'Deleted', 'Updated'];
    const categories = [
        'Unknown',
        'Product',
        'Procedure',
        'Safety Hazard',
        'Coating',
        'Coating Note',
        'Coating Manufacturer',
        'Draeger Tube',
        'Category Document',
        'USCG Group to Product',
        'Inter Product Compatibility',
        'Stowage Rule',
    ];
    const statuses = ['Pending Review', 'Reviewed'];
    const users = Array.from(new Set(allChangeLogEntries.map((entry) => entry.userName))).sort();

    // Client-side filtering on loaded data
    const filteredEntries = useMemo(() => {
        return allChangeLogEntries.filter((entry) => {
            const matchesType = !selectedType || entry.typeName === selectedType;
            const matchesCategory = !selectedCategory || entry.categoryName === selectedCategory;
            const matchesUser = !selectedUser || entry.userName === selectedUser;
            const matchesStatus = !selectedStatus || entry.statusName === selectedStatus;

            let matchesDate = true;
            if (endDateFilter) {
                const filterDate = new Date(endDateFilter);
                filterDate.setHours(0, 0, 0, 0);
                matchesDate = entry.date >= filterDate;
            }

            return matchesType && matchesCategory && matchesUser && matchesStatus && matchesDate;
        });
    }, [selectedType, selectedCategory, selectedUser, selectedStatus, endDateFilter, allChangeLogEntries]);

    const hasMore = loadedCount < totalCount;

    return (
        <SectionCard>

            {error && <Error>Error loading change sets: {typeof error === 'object' && error !== null && 'message' in error ? (error as Error).message : String(error)}</Error>}

            {/* Filter bar */}
            <FilterBar>
                <ul>
                    <li>
                        <FilterLabel htmlFor="productChangelogYear">Year:</FilterLabel>
                        <EditSelect
                            id="productChangelogYear"
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                            aria-label="Filter by year"
                        >
                            {yearOptions.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </EditSelect>
                    </li>
                    <li>
                        <FilterLabel htmlFor="productChangelogSort">Sort:</FilterLabel>
                        <EditSelect
                            id="productChangelogSort"
                            value={selectedSort}
                            onChange={(e) => setSelectedSort(e.target.value)}
                            aria-label="Sort order"
                        >
                            <option value="date-desc">Newest first</option>
                            <option value="date-asc">Oldest first</option>
                        </EditSelect>
                    </li>
                    <li className="spacer" />
                    <li className="load-more-info">
                        <span>
                            {filteredEntries.length} of {totalCount}
                        </span>
                    </li>
                    {hasMore && (
                        <li>
                            <LoadMoreButton onClick={handleLoadMore} disabled={loading}>
                                {loading ? 'Loading...' : `Load ${LOAD_MORE_COUNT} more`}
                            </LoadMoreButton>
                        </li>
                    )}
                </ul>
            </FilterBar>

            {/* Table */}
            <BorderedContainer>
                <HeaderRow>
                    <div className="changeType">
                        <EditSelect value={selectedType} onChange={(e) => setSelectedType(e.target.value)} aria-label="Filter by change type">
                            <option value="">Type</option>
                            {types.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </EditSelect>
                    </div>
                    <div className="dateTime">
                        <EditInput
                            type="date"
                            gradientBackground
                            value={endDateFilter}
                            onChange={(e) => setEndDateFilter(e.target.value)}
                            aria-label="Filter from date"
                        />
                    </div>
                    <div className="category">
                        <EditSelect value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} aria-label="Filter by category">
                            <option value="">Category</option>
                            {categories.map((category) => (
                                <option key={category} value={category}>
                                    {category}
                                </option>
                            ))}
                        </EditSelect>
                    </div>
                    <div></div>
                    <div className="changedBy">
                        <EditSelect value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} aria-label="Filter by user">
                            <option value="">User</option>
                            {users.map((user) => (
                                <option key={user} value={user}>
                                    {user}
                                </option>
                            ))}
                        </EditSelect>
                    </div>
                    <div className="changeStatus">
                        <EditSelect value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} aria-label="Filter by status">
                            <option value="">Status</option>
                            {statuses.map((status) => (
                                <option key={status} value={status}>
                                    {status}
                                </option>
                            ))}
                        </EditSelect>
                    </div>
                </HeaderRow>
                <TableScrollContainer>
                    {loading && loadedCount === 0 && <LoadingSpinner text="Loading change sets..." />}
                    {!loading && !error && filteredEntries.length === 0 && <NoData>No change sets found for this product.</NoData>}
                    {filteredEntries.length > 0 &&
                        filteredEntries.map((entry) => (
                            <ChangeRow
                                key={entry.id}
                                clickable
                                onClick={() => handleRowClick(entry.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && handleRowClick(entry.id)}
                            >
                                <div className="changeType">
                                    <TypeBadge variant={entry.typeName.toLowerCase() as 'created' | 'updated' | 'deleted'}>
                                        {entry.typeName}
                                    </TypeBadge>
                                </div>
                                <div className="dateTime">{formatDateTime(entry.date)}</div>
                                <div className="category">{entry.categoryName}</div>
                                <HideOverflow className="itemChanged">{entry.displayInformation}</HideOverflow>
                                <div className="changedBy">
                                    <UserCell>
                                        <UserAvatar>{getInitials(entry.userName)}</UserAvatar>
                                        <HideOverflow>{entry.userName}</HideOverflow>
                                    </UserCell>
                                </div>
                                <div className="changeStatus">
                                    <StatusBadge status={entry.status === ChangeSetStatusEnum.Reviewed ? 'reviewed' : 'pending'}>
                                        <FontAwesomeIcon icon={entry.status === ChangeSetStatusEnum.Reviewed ? faCheck : faClock} />
                                        {entry.statusName}
                                    </StatusBadge>
                                </div>
                            </ChangeRow>
                        ))}
                    {loading && loadedCount > 0 && <LoadingSpinner text="Loading more..." />}
                </TableScrollContainer>
            </BorderedContainer>
        </SectionCard>
    );
};

export default ProductChangeLogSection;
